import { useQuery } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';

import { BrokerWizard } from '@/features/brokers/BrokerWizard';
import {
  canaisDoAssistente,
  dadosDoRascunhoDoAssistente,
  pedidoPublicoDoAssistente,
} from '@/features/brokers/assistenteDoCorretor';
import { ApiError } from '@/shared/api/client';
import {
  brokerSelfSignup,
  type BrokerRegistrationCheck,
  type BrokerSelfSignupReceipt,
} from '@/shared/api/resources';
import { ErrorCode } from '@/shared/api/types';
import { formatDocument } from '@/shared/lib/format';
import { Alert } from '@/shared/ui/Alert';
import { LogoHorizontal } from '@/shared/ui/Logo';
import { Spinner } from '@/shared/ui/Spinner';

import { segredoDoLink } from './autocadastroLink';
import { usePortalConfig } from './usePortalConfig';

/**
 * O link vence no prazo da criacao, mas o que ja foi gravado fica guardado por um prazo
 * proprio: o novo inicio com o mesmo CNPJ e o mesmo e-mail manda outro link, e o
 * assistente volta com o rascunho (R5-M11).
 */
const LINK_VENCIDO =
  'O link do cadastro nao e valido ou venceu. O que voce ja gravou fica guardado por ' +
  'alguns dias: inicie o cadastro de novo com o mesmo CNPJ e o mesmo e-mail para ' +
  'receber outro link e continuar de onde parou.';

function linkVencido(erro: unknown): boolean {
  return (
    erro instanceof ApiError &&
    erro.code === ErrorCode.NotFound &&
    erro.details?.['reason'] === 'link_invalido'
  );
}

function mensagemDoLink(erro: unknown): string {
  if (erro instanceof ApiError && erro.code === ErrorCode.NotFound) {
    return erro.details?.['reason'] === 'autocadastro_indisponivel'
      ? 'O autocadastro de corretora nao esta mais disponivel para esta empresa.'
      : LINK_VENCIDO;
  }
  if (erro instanceof ApiError && erro.code === ErrorCode.RateLimited) {
    return 'Muitas tentativas em pouco tempo. Aguarde um instante e abra o link de novo.';
  }
  return 'Nao foi possivel abrir o cadastro. Tente de novo pelo link do e-mail.';
}

/**
 * Assistente do autocadastro da corretora (o /completar-cadastro/:cnpj do OnPoint,
 * USU-098 a USU-138), aberto pelo link enviado ao e-mail.
 *
 * Abrir o link confirma o e-mail e traz o rascunho e as listas do assistente. O
 * assistente e o mesmo da seguradora (`/corretores/novo`), com a conta e as pessoas
 * liberadas: quem decide o que vale e a seguradora, na aprovacao. Cada etapa conferida
 * grava o rascunho; o "Concluir cadastro" manda o pedido para a esteira Cadastro.
 */
export function BrokerSelfSignupWizardPage() {
  const { registrationId = '' } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const token = useMemo(() => segredoDoLink(location.hash), [location.hash]);
  const [concluido, setConcluido] = useState<BrokerSelfSignupReceipt | null>(null);
  // A gravacao do rascunho que voltou com o link vencido: o assistente avisa, em vez de
  // seguir como se tivesse gravado (R5-M11).
  const [rascunhoSemLink, setRascunhoSemLink] = useState(false);

  const aberto = useQuery({
    queryKey: ['autocadastro', registrationId, token],
    queryFn: () => brokerSelfSignup.confirm(registrationId, token),
    enabled: registrationId !== '' && token !== '',
    retry: false,
    staleTime: Infinity,
  });
  const pedido = aberto.data;
  const { portal } = usePortalConfig(pedido?.tenant_slug);
  const reconhecida = portal?.resolved ? portal : null;
  const voltar = pedido?.tenant_slug
    ? `/entrar/${encodeURIComponent(pedido.tenant_slug)}`
    : '/entrar';

  const dadosIniciais = useMemo(
    () =>
      pedido ? dadosDoRascunhoDoAssistente(pedido.document, pedido.draft) : undefined,
    [pedido],
  );

  function verificarCnpj(cnpj: string): Promise<BrokerRegistrationCheck> {
    if (!pedido || cnpj !== pedido.document) {
      return Promise.reject(
        new Error(
          `O CNPJ do cadastro e o informado no inicio: ${formatDocument(pedido?.document ?? '')}.`,
        ),
      );
    }
    return Promise.resolve({
      document: pedido.document,
      already_registered: false,
      broker: null,
      company_lookup: {
        status: 'nao_configurada',
        provider: '',
        message:
          'A consulta a Receita Federal nao esta configurada. Preencha os dados da corretora.',
        data: null,
      },
    });
  }

  let conteudo;
  if (!token) {
    conteudo = (
      <Alert tone="warning" title="Link incompleto">
        Abra o cadastro pelo link enviado ao seu e-mail.
      </Alert>
    );
  } else if (aberto.isPending) {
    conteudo = (
      <div className="flex justify-center py-16">
        <Spinner />
      </div>
    );
  } else if (aberto.isError || !pedido) {
    conteudo = (
      <div className="flex flex-col gap-4">
        <Alert tone="danger" title="Nao foi possivel abrir o cadastro">
          {mensagemDoLink(aberto.error)}
        </Alert>
        <Link
          to={voltar}
          className="text-sm font-medium text-brand-600 hover:underline"
        >
          Voltar para o acesso
        </Link>
      </div>
    );
  } else if (concluido) {
    conteudo = (
      <div className="flex flex-col gap-4">
        <Alert tone="success" title="Cadastro concluido">
          {concluido.message}
        </Alert>
        <Link
          to={voltar}
          className="text-sm font-medium text-brand-600 hover:underline"
        >
          Voltar para o acesso
        </Link>
      </div>
    );
  } else {
    conteudo = (
      <BrokerWizard<BrokerSelfSignupReceipt>
        canais={canaisDoAssistente(pedido.options.channels)}
        perfis={pedido.options.roles}
        podeAlterarConta
        podeConvidar
        verificarCnpj={verificarCnpj}
        concluir={(dados) =>
          brokerSelfSignup.complete(
            registrationId,
            token,
            pedidoPublicoDoAssistente(dados),
          )
        }
        onConcluido={setConcluido}
        onCancelar={() => void navigate(voltar)}
        cnpjInicial={pedido.document}
        dadosIniciais={dadosIniciais}
        bancos={pedido.options.banks}
        aoAvancar={(dados) => {
          // O rascunho e conveniencia: a falha passageira ao grava-lo nao para o
          // assistente, e o "Concluir cadastro" envia tudo de novo. O link vencido nao e
          // passageiro: o concluir tambem seria recusado, e a tela avisa como continuar.
          brokerSelfSignup
            .saveDraft(registrationId, token, pedidoPublicoDoAssistente(dados))
            .then(() => setRascunhoSemLink(false))
            .catch((erro: unknown) => {
              if (linkVencido(erro)) setRascunhoSemLink(true);
            });
        }}
      />
    );
  }

  const logo = reconhecida?.layout.common.logo;
  return (
    <div className="min-h-dvh bg-canvas">
      <header className="border-b border-line bg-surface">
        <div className="mx-auto flex max-w-4xl items-center gap-3 px-5 py-4">
          {logo ? (
            <img
              src={logo}
              alt={reconhecida?.display_name ?? ''}
              className="h-10 w-auto"
            />
          ) : (
            <LogoHorizontal height={40} />
          )}
          <span className="text-sm text-ink-500">
            {reconhecida?.display_name || pedido?.tenant_name || ''}
          </span>
        </div>
      </header>
      <main className="mx-auto flex max-w-4xl flex-col gap-5 px-5 py-8">
        <div>
          <h1 className="text-xl font-semibold text-ink-900">Cadastro de corretora</h1>
          {pedido ? (
            <p className="mt-1 text-sm text-ink-500">
              CNPJ {formatDocument(pedido.document)}. O link foi enviado a{' '}
              {pedido.email_masked}.
            </p>
          ) : null}
        </div>
        {rascunhoSemLink && !concluido ? (
          <Alert tone="warning" title="O link do cadastro venceu">
            {LINK_VENCIDO}
          </Alert>
        ) : null}
        {conteudo}
      </main>
    </div>
  );
}
